import React, { useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CategoryFilter } from '../components/careers/CategoryFilter';
import { SearchBar } from '../components/careers/SearchBar';
import { JobList } from '../components/careers/JobList';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { jobs } from '../data/jobs';
import { useJobFilter } from '../hooks/useJobFilter';
import { useJobSearch } from '../hooks/useJobSearch';

export const CareersPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCategory, setSelectedCategory, filteredJobs } = useJobFilter(jobs);
  const { searchTerm, setSearchTerm, searchedJobs } = useJobSearch(filteredJobs);

  const finalJobs = useMemo(() => searchedJobs, [searchedJobs]);

  const menuItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Enterprise API', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleJobClick = (job: any) => {
    navigate(`/careers/job/${job.id}`);
  };

  return (
    <div className="min-h-screen gradient-bg">
      <Helmet>
        <title>Careers at Cura – Join Our Healthcare AI Team</title>
        <meta name="description" content="Build technology that transforms senior healthcare. Explore open positions at Cura and help us make medical guidance accessible for everyone ✓" />
        <link rel="canonical" href="https://mycura.org/careers" />
        <meta property="og:title" content="Careers at Cura – Join Our Healthcare AI Team" />
        <meta property="og:description" content="Build technology that transforms senior healthcare. Explore open positions at Cura and help us make medical guidance accessible for everyone." />
        <meta property="og:url" content="https://mycura.org/careers" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Careers at Cura – Join Our Healthcare AI Team" />
        <meta name="twitter:description" content="Build technology that transforms senior healthcare. Explore open positions at Cura and help us make medical guidance accessible for everyone." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cura Careers",
            "url": "https://mycura.org/careers",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
              { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://mycura.org/careers" }
            ]
          })}
        </script>
      </Helmet>
      <Navigation
        menuItems={menuItems}
        scrollToSection={scrollToSection}
        scrollToTop={scrollToTop}
      />

      {/* Header Section */}
      <div className="relative overflow-hidden pt-32">
        <div className="container mx-auto px-6 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            {/* Logo and Title */}
            <div className="mb-8">
              <div className="flex justify-center mb-4">
                <img
                  src="/images/Long Logo.png"
                  alt="Cura"
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-2xl text-gray-800 font-light">Join Our Team</p>
            </div>

            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Help us revolutionize healthcare for seniors. Join a passionate team
              building technology that makes healthcare more accessible, personalized,
              and effective for those who need it most.
            </p>
          </motion.div>

          {/* Category Filter */}
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />

          {/* Search Bar */}
          <div className="mb-8">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="container mx-auto px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <JobList jobs={finalJobs} onJobClick={handleJobClick} />
        </div>
      </div>

      {/* About Cura Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="container mx-auto px-6 pb-20"
      >
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">About Cura</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Cura is transforming healthcare for seniors by making it more accessible,
              personalized, and effective. Our mission is to empower seniors and their
              families with the tools and support they need to navigate their healthcare
              journey with confidence.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We believe that technology should serve humanity, especially those who
              need it most. Join us in building a future where every senior can access
              quality healthcare with dignity and ease.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer />
    </div>
  );
};
